import { User } from '@protos/user';
import { useUserSettings } from '@shared/contexts/UserContextProvider';
import { checkIsABNUser } from '@utils/permissions';
import { useEffect, useMemo, useState } from 'react';

export const useTradingPermissions = (user: User | undefined, payloadValue?: string) => {
  const [selectedTradingAccount, setSelectedTradingAccount] = useState<string | undefined>(undefined);
  const userSettings = useUserSettings();

  const ABNTradingAccounts = useMemo(() => {
    if (!user) return [];
    return user.abn_trad_accounts;
  }, [user]);

  const userTradeLimits = useMemo(() => {
    if (!user?.trade_limits) return [];
    if (!userSettings?.ocl_trading?.trade_limits) return user?.trade_limits || [];
    if (user?.trade_limits && user.trade_limits.length === 0) return [];
    return user.trade_limits;

    //==========================================
    // TODO: determine what the merge issue is
    //==========================================

    // const settingsLimits = userSettings.ocl_trading.trade_limits;

    // const userSymbols = new Set(userLimits.map(limit => limit.symbol));
    // const settingsSymbols = new Set(settingsLimits.map(limit => limit.symbol));

    // const hasSameSymbols = userSymbols.size === settingsSymbols.size && [...userSymbols].every(symbol => settingsSymbols.has(symbol));

    // if (hasSameSymbols) {
    //   return settingsLimits;
    // }
    // const mergedLimits: TradeLimit[] = userLimits.map(mergedLimit => {
    //   if (settingsSymbols.has(mergedLimit.symbol)) {
    //     return settingsLimits.find(settingsLimit => settingsLimit.symbol === mergedLimit.symbol) || mergedLimit;
    //   }
    //   return mergedLimit;
    // });

    // return mergedLimits;
  }, [user, userSettings]);

  const externalTradingAccounts = useMemo(() => {
    if (!user) return [];
    return user.trading_accounts ?? [];
  }, [user]);

  const tradingAccounts = useMemo(() => {
    if (!user) return [];

    const sourceAccounts = checkIsABNUser(user) ? ABNTradingAccounts : externalTradingAccounts;

    const userSettingsAccounts = userSettings?.ocl_trading?.trade_accounts;
    // return sourceAccounts;
    if (!userSettingsAccounts) return sourceAccounts;

    if (sourceAccounts.length === 0) return [];

    const sourceAccountSet = new Set(sourceAccounts);
    const settingsAccountSet = new Set(userSettingsAccounts);

    const isCompatible = settingsAccountSet.size === sourceAccountSet.size && [...settingsAccountSet].every(account => sourceAccountSet.has(account));

    if (isCompatible) {
      return userSettingsAccounts;
    }

    const mergedAccounts = [...sourceAccounts];
    const validSettingsAccounts = userSettingsAccounts.filter(account => sourceAccountSet.has(account));

    validSettingsAccounts.forEach(account => {
      if (!mergedAccounts.includes(account)) {
        mergedAccounts.push(account);
      }
    });

    return mergedAccounts;
  }, [ABNTradingAccounts, externalTradingAccounts, userSettings?.ocl_trading?.trade_accounts]);

  const defaultTradeSizes = useMemo(() => {
    return userSettings?.ocl_trading?.default_trade_sizes || {};
  }, [userSettings]);

  useEffect(() => {
    const defaultTradingAccount = tradingAccounts[0];
    const selectedTradingAccount = tradingAccounts.find(account => account === payloadValue) || defaultTradingAccount;

    setSelectedTradingAccount(selectedTradingAccount);
  }, [tradingAccounts]);

  return {
    tradingAccounts,
    userTradeLimits,
    selectedTradingAccount,
    setSelectedTradingAccount,
    defaultTradeSizes,
  };
};
